import React, { useState } from 'react';
import api from '../../utils/api';
import { toast } from 'react-toastify';
import { Upload, File, X } from 'lucide-react';

const EvidenceUpload = ({ caseId, onSuccess }) => {
  const [file, setFile] = useState(null);
  const [description, setDescription] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      // Allow images, videos, PDF, and CSV
      const allowedTypes = [
        // Images
        'image/jpeg',
        'image/jpg',
        'image/png',
        'image/gif',
        'image/webp',
        // Videos
        'video/mp4',
        'video/avi',
        'video/quicktime',
        'video/x-msvideo',
        'video/x-ms-wmv',
        'video/mpeg',
        'video/webm',
        // Documents
        'application/pdf',
        'text/csv',
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      ];
      
      if (!allowedTypes.includes(selectedFile.type)) {
        toast.error('Only images, videos, PDF, CSV, and Excel files are allowed');
        return;
      }
      
      // Increase file size limit for videos (50MB for videos, 10MB for others)
      const maxSize = selectedFile.type.startsWith('video/') ? 50 * 1024 * 1024 : 10 * 1024 * 1024;
      if (selectedFile.size > maxSize) {
        const maxSizeMB = maxSize / (1024 * 1024);
        toast.error(`File size must be less than ${maxSizeMB}MB`);
        return;
      }
      setFile(selectedFile);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file) {
      toast.error('Please select a file');
      return;
    }

    try {
      setLoading(true);
      const formData = new FormData();
      formData.append('file', file);
      formData.append('caseId', caseId);
      formData.append('description', description);
      if (password) {
        formData.append('password', password);
      }

      await api.post('/evidence', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      toast.success('Evidence uploaded successfully');
      setFile(null);
      setDescription('');
      setPassword('');
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to upload evidence');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Evidence File <span className="text-red-500">*</span>
        </label>
        <div className="flex items-center space-x-4">
          <label className="flex-1 cursor-pointer">
            <input
              type="file"
              onChange={handleFileChange}
              accept=".jpeg,.jpg,.png,.gif,.webp,.pdf,.csv,.xlsx,.mp4,.avi,.mov,.wmv,.mpeg,.webm"
              className="hidden"
            />
            <div className="flex items-center justify-center px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-500 transition-colors">
              <Upload className="w-5 h-5 text-gray-400 mr-2" />
              <span className="text-sm text-gray-600">
                {file ? file.name : 'Click to select file (Images, Videos, PDF, CSV, Excel)'}
              </span>
            </div>
          </label>
          {file && (
            <button
              type="button"
              onClick={() => setFile(null)}
              className="p-2 text-red-600 hover:bg-red-50 rounded"
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>
        <p className="mt-1 text-xs text-gray-500">
          Maximum file size: 10MB (images/PDF/CSV/Excel), 50MB (videos). Allowed types: Images (JPEG, PNG, GIF), Videos (MP4, AVI, MOV, WMV, MPEG, WEBM), PDF, CSV, Excel (XLSX)
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Description
        </label>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={3}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          placeholder="Enter evidence description"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Password (Optional)
        </label>
        <div className="relative">
          <input
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="Leave empty for no password"
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
          >
            {showPassword ? 'Hide' : 'Show'}
          </button>
        </div>
        <p className="mt-1 text-xs text-gray-500">
          If set, this password will be required to view or download the evidence. Leave empty for direct access.
        </p>
      </div>

      <div className="flex items-center justify-end space-x-3">
        <button
          type="button"
          onClick={() => {
            setFile(null);
            setDescription('');
            setPassword('');
            onSuccess();
          }}
          className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={loading || !file}
          className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
        >
          <Upload className="w-4 h-4" />
          <span>{loading ? 'Uploading...' : 'Upload Evidence'}</span>
        </button>
      </div>
    </form>
  );
};

export default EvidenceUpload;

